import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/api/client";
import ProductCard from "@/components/products/ProductCard";
import { useI18n } from "@/contexts/I18nContext";
import { Store, ChevronRight, Package } from "lucide-react";
import SeoHead from "@/components/seo/SeoHead";

const BrandDetail = () => {
  const { slug } = useParams();
  const { t } = useI18n();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/brands/${slug}`)
      .then((r) => setBrand(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="container-fah py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-muted/60 rounded-xl" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] bg-muted/60 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !brand) {
    return (
      <div className="container-fah py-20 text-center">
        <Store className="w-12 h-12 mx-auto text-muted-foreground" />
        <h2 className="font-display text-2xl font-bold mt-3">{t("Brand not found")}</h2>
        <Link to="/brands" className="mt-4 inline-block text-primary underline">
          {t("Browse all brands")}
        </Link>
      </div>
    );
  }

  return (
    <div className="container-fah py-6 sm:py-8" data-testid="page-brand-detail">
      <SeoHead
        title={`${brand.name} — Products on FarmersAgroHub`}
        description={brand.description || `Shop genuine ${brand.name} products on FarmersAgroHub — verified quality, Pan-India delivery.`}
        path={`/brand/${brand.slug}`}
      />

      {/* Breadcrumb */}
      <div className="text-xs text-muted-foreground flex items-center gap-1 mb-4">
        <Link to="/" className="hover:text-primary">{t("Home")}</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to="/brands" className="hover:text-primary">{t("Brands")}</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-foreground">{brand.name}</span>
      </div>

      {/* Hero */}
      <div className="card-fah p-5 sm:p-6 mb-6 flex items-center gap-4 sm:gap-6" data-testid="brand-hero">
        <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-full bg-accent/40 border border-border overflow-hidden flex items-center justify-center shrink-0">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={brand.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-4xl sm:text-5xl font-display font-bold text-primary">
              {brand.name.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-bold">{brand.name}</h1>
          {brand.description && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-3">{brand.description}</p>
          )}
          <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-primary bg-accent/40 rounded-full px-2.5 py-1">
            <Package className="w-3.5 h-3.5" />
            {brand.product_count} {brand.product_count === 1 ? "product" : "products"}
          </div>
        </div>
      </div>

      {/* Products */}
      {brand.products.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="brand-no-products">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>{t("No products available under this brand yet.")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="brand-products">
          {brand.products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
};

export default BrandDetail;
